import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  ChevronLeft, Calendar, Clock, Building2, MapPin,
  FileText, Paperclip, Check, RotateCcw
} from 'lucide-react';
import { todayStr, toDateStr } from '../utils/dateUtils';

/* ─── Quill Rich Text Editor (lazy load agar tidak crash SSR) ─── */
let ReactQuill = null;
try {
  ReactQuill = require('react-quill').default;
  require('react-quill/dist/quill.snow.css');
} catch (e) { /* fallback ke textarea */ }

const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet'];

/* Komponen editor keterangan – pakai Quill jika tersedia, fallback textarea */
function KeteranganEditor({ value, onChange, placeholder }) {
  if (ReactQuill) {
    return (
      <div className="quill-wrapper">
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder={placeholder}
        />
      </div>
    );
  }
  return (
    <textarea
      className="form-input"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={4}
    />
  );
}

const makeEmpty = (tgl) => ({
  tanggal: tgl || todayStr(),
  jam_mulai: '', jam_selesai: '', is_selesai_tbd: false,
  nama_acara: '', penyelenggara: '', lokasi: '',
  no_surat: '', keterangan: '',
  is_disposisi: false, disposisi_ket: '',
  is_gubernur: false,
});

export default function InputJadwalPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const defaultTanggal = searchParams.get('tanggal');
  const isEdit = !!editId;

  const [form, setForm] = useState(makeEmpty(defaultTanggal));
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isEdit) return;
    setLoadingEdit(true);
    api.get(`/jadwal/${editId}`)
      .then(res => {
        const j = res.data.data;
        setForm({
          tanggal: toDateStr(j.tanggal),
          jam_mulai: j.jam_mulai?.slice(0, 5) || '',
          jam_selesai: j.jam_selesai?.slice(0, 5) || '',
          is_selesai_tbd: !!j.is_selesai_tbd,
          nama_acara: j.nama_acara || '',
          penyelenggara: j.penyelenggara || '',
          lokasi: j.lokasi || '',
          no_surat: j.no_surat || '',
          keterangan: j.keterangan || '',
          is_disposisi: !!j.is_disposisi,
          disposisi_ket: j.disposisi_ket || '',
          is_gubernur: !!j.is_gubernur,
        });
        setExistingFile(j.lampiran);
      })
      .catch(() => toast.error('Gagal memuat data jadwal'))
      .finally(() => setLoadingEdit(false));
  }, [editId, isEdit]);

  const set = field => e => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors(er => ({ ...er, [field]: '' }));
  };
  const toggle = field => () => setForm(f => ({ ...f, [field]: !f[field] }));

  const validate = () => {
    const e = {};
    if (!form.tanggal) e.tanggal = 'Tanggal wajib diisi';
    if (!form.jam_mulai) e.jam_mulai = 'Jam mulai wajib diisi';
    if (!form.nama_acara.trim()) e.nama_acara = 'Nama acara wajib diisi';
    if (!form.penyelenggara.trim()) e.penyelenggara = 'Penyelenggara wajib diisi';
    if (!form.lokasi.trim()) e.lokasi = 'Lokasi wajib diisi';
    if (!form.is_selesai_tbd && form.jam_selesai && form.jam_selesai <= form.jam_mulai)
      e.jam_selesai = 'Jam selesai harus setelah jam mulai';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('tanggal', form.tanggal);
      fd.append('jam_mulai', form.jam_mulai);
      fd.append('jam_selesai', form.is_selesai_tbd ? '' : form.jam_selesai);
      fd.append('is_selesai_tbd', form.is_selesai_tbd ? '1' : '0');
      fd.append('nama_acara', form.nama_acara);
      fd.append('penyelenggara', form.penyelenggara);
      fd.append('lokasi', form.lokasi);
      fd.append('no_surat', form.no_surat);
      // Keterangan dari Quill (bisa berisi HTML)
      fd.append('keterangan', form.keterangan);
      fd.append('is_disposisi', form.is_disposisi ? '1' : '0');
      fd.append('disposisi_ket', form.is_disposisi ? form.disposisi_ket : '');
      fd.append('is_gubernur', form.is_gubernur ? '1' : '0');
      if (file) fd.append('lampiran', file);

      if (isEdit) {
        await api.put(`/jadwal/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Jadwal berhasil diperbarui!');
      } else {
        await api.post('/jadwal', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Jadwal berhasil disimpan!');
      }
      navigate('/jadwal');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menyimpan jadwal');
    } finally { setLoading(false); }
  };

  const handleReset = () => { setForm(makeEmpty(defaultTanggal)); setFile(null); setErrors({}); };

  if (loadingEdit) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <div className="loader" style={{ borderColor: 'var(--gray-200)', borderTopColor: 'var(--primary)', width: 32, height: 32, borderWidth: 3 }} />
    </div>
  );

  return (
    <div className="page-container" style={{ maxWidth: 760 }}>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate(-1)}>
        <ChevronLeft size={16} /> Kembali
      </button>

      <div style={{ background: 'linear-gradient(135deg, var(--secondary), var(--secondary-light))', borderRadius: 'var(--radius-lg)', padding: '28px 32px', color: 'white', marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>{isEdit ? 'Edit Jadwal Acara' : 'Input Jadwal Acara'}</h1>
        <p style={{ opacity: 0.8, fontSize: 14, marginTop: 4 }}>{isEdit ? 'Perbarui data agenda' : 'Tambahkan agenda baru'}</p>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Tanggal & Jam */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label"><Calendar size={14} /> Tanggal <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className={`form-input ${errors.tanggal ? 'input-error' : ''}`} type="date" value={form.tanggal} onChange={set('tanggal')} />
                {errors.tanggal && <span className="field-error">{errors.tanggal}</span>}
              </div>
              <div className="form-group">
                <label className="form-label"><Clock size={14} /> Jam Mulai <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className={`form-input ${errors.jam_mulai ? 'input-error' : ''}`} type="time" value={form.jam_mulai} onChange={set('jam_mulai')} />
                {errors.jam_mulai && <span className="field-error">{errors.jam_mulai}</span>}
              </div>
              <div className="form-group">
                <label className="form-label"><Clock size={14} /> Jam Selesai</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', userSelect: 'none', marginBottom: 6 }}>
                  <input type="checkbox" checked={form.is_selesai_tbd} onChange={toggle('is_selesai_tbd')}
                    style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }} />
                  <span style={{ color: form.is_selesai_tbd ? 'var(--primary)' : 'var(--gray-500)', fontWeight: form.is_selesai_tbd ? 600 : 400 }}>
                    Selesai (tidak ditentukan)
                  </span>
                </label>
                <input className={`form-input ${errors.jam_selesai ? 'input-error' : ''}`} type="time"
                  value={form.jam_selesai} onChange={set('jam_selesai')}
                  disabled={form.is_selesai_tbd} style={{ opacity: form.is_selesai_tbd ? 0.4 : 1 }} />
                {errors.jam_selesai && <span className="field-error">{errors.jam_selesai}</span>}
              </div>
            </div>

            {/* Nama Acara */}
            <div className="form-group">
              <label className="form-label"><FileText size={14} /> Nama Acara <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className={`form-input ${errors.nama_acara ? 'input-error' : ''}`} type="text"
                placeholder="Masukkan nama acara" value={form.nama_acara} onChange={set('nama_acara')} />
              {errors.nama_acara && <span className="field-error">{errors.nama_acara}</span>}
            </div>

            {/* Penyelenggara & Lokasi */}
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label"><Building2 size={14} /> Penyelenggara <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className={`form-input ${errors.penyelenggara ? 'input-error' : ''}`} type="text"
                  placeholder="Nama penyelenggara/pengundang" value={form.penyelenggara} onChange={set('penyelenggara')} />
                {errors.penyelenggara && <span className="field-error">{errors.penyelenggara}</span>}
              </div>
              <div className="form-group">
                <label className="form-label"><MapPin size={14} /> Lokasi Acara <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className={`form-input ${errors.lokasi ? 'input-error' : ''}`} type="text"
                  placeholder="Tempat pelaksanaan" value={form.lokasi} onChange={set('lokasi')} />
                {errors.lokasi && <span className="field-error">{errors.lokasi}</span>}
              </div>
            </div>

            {/* No Surat & Lampiran */}
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label"><FileText size={14} /> No Surat</label>
                <input className="form-input" type="text" placeholder="Nomor surat undangan"
                  value={form.no_surat} onChange={set('no_surat')} />
              </div>
              <div className="form-group">
                <label className="form-label"><Paperclip size={14} /> Lampiran</label>
                <label className="upload-area" style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={e => setFile(e.target.files[0])} />
                  <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>
                    {file ? <span style={{ color: 'var(--primary)', fontWeight: 600 }}>📎 {file.name}</span>
                      : existingFile ? <span style={{ color: 'var(--secondary)' }}>📎 {existingFile}</span>
                        : <span>Upload PDF/DOC/JPG/PNG (maks 5MB)</span>}
                  </div>
                </label>
              </div>
            </div>

            {/* Disposisi + Gubernur */}
            <div style={{ border: '1.5px solid var(--gray-200)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', background: form.is_disposisi ? 'var(--secondary-bg)' : 'var(--gray-50)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', flex: 1 }}>
                  <input type="checkbox" checked={form.is_disposisi} onChange={toggle('is_disposisi')}
                    style={{ width: 16, height: 16, accentColor: 'var(--secondary)', cursor: 'pointer' }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: form.is_disposisi ? 'var(--secondary)' : 'var(--gray-700)' }}>
                    Disposisi Acara
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', padding: '6px 12px', background: form.is_gubernur ? '#FEF3C7' : 'white', border: `1.5px solid ${form.is_gubernur ? '#F59E0B' : 'var(--gray-200)'}`, borderRadius: 8 }}>
                  <input type="checkbox" checked={form.is_gubernur} onChange={toggle('is_gubernur')}
                    style={{ width: 15, height: 15, accentColor: '#F59E0B', cursor: 'pointer' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: form.is_gubernur ? '#92400E' : 'var(--gray-600)' }}>
                    ⭐ Undangan Gubernur
                  </span>
                </label>
              </div>
              {form.is_disposisi && (
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--gray-200)', background: 'white' }}>
                  <label className="form-label" style={{ marginBottom: 6, fontSize: 13 }}>Keterangan Disposisi</label>
                  <textarea className="form-input" placeholder="Isi keterangan disposisi undangan..."
                    value={form.disposisi_ket} onChange={set('disposisi_ket')} rows={2} style={{ marginTop: 4, width: '100%' }} />
                </div>
              )}
            </div>

            {/* Keterangan - Quill Rich Text */}
            <div className="form-group">
              <label className="form-label" style={{ marginBottom: 8 }}>
                <FileText size={14} /> Keterangan
                <span style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 400, marginLeft: 6 }}>
                  (mendukung format teks, enter untuk ganti baris)
                </span>
              </label>
              <KeteranganEditor
                value={form.keterangan}
                onChange={(content) => {
                  setForm(f => ({
                    ...f,
                    keterangan: content
                  }));
                }}
                placeholder="Keterangan tambahan, bisa multi-baris dan format teks..."
              />
            </div>

            <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
              <button type="submit" className="btn btn-secondary btn-lg" disabled={loading} style={{ flex: 1 }}>
                {loading ? <span className="loader" /> : <Check size={18} />}
                {loading ? 'Menyimpan...' : (isEdit ? 'Perbarui Jadwal' : 'Simpan Jadwal')}
              </button>
              {!isEdit && (
                <button type="button" className="btn btn-outline btn-lg" onClick={handleReset}>
                  <RotateCcw size={18} /> Reset
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
